/**
 * BlogPost — Single blog post page (blog.example.com/:locale/:slug)
 * Cover, content with markdown rendering, TOC, tags, share, like, related posts
 */

import { T } from '../styles/tokens';
import { h } from 'preact';
import { useState, useEffect, useRef } from 'preact/hooks';
import { Button } from '../components/ui';

const API_BASE = '/api/v1/cms';

interface Post {
  id: string; slug: string; author_name: string; author_avatar: string; is_featured: number;
  cover_image: string; view_count: number;
  like_count: number; tags: string; metadata: string; published_at: string; created_at: string;
  title: string; excerpt: string; body: string; meta_title: string;
  meta_description: string; og_title: string; og_description: string; og_image: string;
  features_json: string; custom_fields: string;
  category_slug: string; category_color: string; category_icon: string; category_name: string;
  category_id: string;
}
interface RelatedPost {
  id: string; slug: string; cover_image: string;
  published_at: string; title: string; excerpt: string; metadata: string;
  category_slug: string; category_color: string; category_name: string;
}
interface TocItem { id: string; text: string; level: number; }

function getLocale(): string {
  const path = window.location.pathname.split('/')[1];
  if (path && (path === 'pt-BR' || path === 'en' || path === 'es')) return path;
  return 'pt-BR';
}

function formatDate(d: string, locale: string): string {
  if (!d) return '';
  try { return new Date(d).toLocaleDateString(locale === 'pt-BR' ? 'pt-BR' : 'en-US', { day: 'numeric', month: 'long', year: 'numeric' }); } catch { return d; }
}

function parseTags(t: string): string[] {
  try { return JSON.parse(t || '[]'); } catch { return []; }
}

/** Simple markdown to HTML (headings, bold, italic, code, links, images, lists, blockquotes, tables) */
function renderMarkdown(md: string): { html: string; toc: TocItem[] } {
  const toc: TocItem[] = [];
  let html = md
    // Code blocks
    .replace(/```(\w*)\n([\s\S]*?)```/g, (_: string, lang: string, code: string) => {
      const escaped = code.replace(/</g, '&lt;').replace(/>/g, '&gt;');
      return `<pre class="blog-code-block"><code class="language-${lang}">${escaped}</code></pre>`;
    })
    // Inline code
    .replace(/`([^`]+)`/g, '<code class="blog-inline-code">$1</code>')
    // Images
    .replace(/!\[([^\]]*)\]\(([^)]+)\)/g, '<img class="blog-img" src="$2" alt="$1" loading="lazy" />')
    // Links
    .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" class="blog-link" target="_blank" rel="noopener">$1</a>')
    // Bold
    .replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>')
    // Italic
    .replace(/\*([^*]+)\*/g, '<em>$1</em>')
    // Headings
    .replace(/^### (.+)$/gm, (_: string, text: string) => {
      const id = text.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/-+$/, '');
      toc.push({ id, text, level: 3 });
      return `<h3 id="${id}" class="blog-h3">${text}</h3>`;
    })
    .replace(/^## (.+)$/gm, (_: string, text: string) => {
      const id = text.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/-+$/, '');
      toc.push({ id, text, level: 2 });
      return `<h2 id="${id}" class="blog-h2">${text}</h2>`;
    })
    // Blockquotes
    .replace(/^> (.+)$/gm, '<blockquote class="blog-quote">$1</blockquote>')
    // Tables
    .replace(/^\|(.+)\|$/gm, (line: string) => {
      if (line.match(/^\|[\s-|]+\|$/)) return '<!--table-sep-->';
      const cells = line.split('|').filter(Boolean).map(c => c.trim());
      return '<tr>' + cells.map(c => `<td>${c}</td>`).join('') + '</tr>';
    })
    // Unordered lists
    .replace(/^- (.+)$/gm, '<li class="blog-li">$1</li>')
    // Paragraphs (double newlines)
    .replace(/\n\n/g, '</p><p class="blog-p">')
    // Single newlines inside paragraphs
    .replace(/\n/g, '<br/>');

  // Wrap in paragraphs
  html = '<p class="blog-p">' + html + '</p>';
  // Clean up
  html = html.replace(/<p class="blog-p"><\/p>/g, '');
  html = html.replace(/<!--table-sep-->/g, '');
  // Wrap consecutive <tr> in <table>
  html = html.replace(/(<tr>[\s\S]*?<\/tr>(?:\s*<tr>[\s\S]*?<\/tr>)*)/g, '<table class="blog-table">$1</table>');
  // Wrap consecutive <li> in <ul>
  html = html.replace(/(<li[\s\S]*?<\/li>(?:\s*<li[\s\S]*?<\/li>)*)/g, '<ul class="blog-ul">$1</ul>');

  return { html, toc };
}

const S = {
  page: { minHeight: '100vh', background: T.bg, color: T.text, fontFamily: "'Inter', -apple-system, sans-serif" } as any,
  container: { maxWidth: 1200, margin: '0 auto', padding: '0 20px' } as any,
  header: { padding: '20px 0', borderBottom: `1px solid ${T.border}`, marginBottom: 0, display: 'flex', alignItems: 'center', gap: 8 } as any,
  logo: { fontSize: 20, fontWeight: 700, color: T.text, textDecoration: 'none', display: 'flex', alignItems: 'center', gap: 8 } as any,
  logoAccent: { background: `linear-gradient(135deg, ${T.primary}, ${T.purple})`, WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' } as any,
  cover: { position: 'relative' as const, width: '100%', minHeight: 400, display: 'flex', alignItems: 'flex-end', background: `linear-gradient(135deg, #1a1e3a 0%, ${T.bg} 100%)`, marginBottom: 40 } as any,
  coverOverlay: { position: 'absolute' as const, inset: 0, background: `linear-gradient(180deg, transparent 0%, rgba(10,14,26,0.8) 70%, ${T.bg} 100%)` } as any,
  coverContent: { position: 'relative' as const, padding: '60px 32px 40px', zIndex: 1, maxWidth: 800 } as any,
  coverTitle: { fontSize: 40, fontWeight: 800, lineHeight: 1.2, margin: '12px 0 16px', textShadow: `0 2px 20px ${T.blackAlpha50}` } as any,
  coverMeta: { display: 'flex', alignItems: 'center', gap: 16, fontSize: 14, color: T.textSecondary, flexWrap: 'wrap' as const } as any,
  avatar: { width: 36, height: 36, borderRadius: '50%', border: `2px solid ${T.borderHover}`, background: `linear-gradient(135deg, ${T.primary}, ${T.purple})`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14, fontWeight: 700 } as any,
  badge: (color: string) => ({ display: 'inline-block', padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 600, background: `${color}20`, color }) as any,
  readBadge: { display: 'inline-flex', alignItems: 'center', gap: 4, padding: '3px 10px', borderRadius: 20, fontSize: 11, background: T.border, color: T.textSecondary } as any,
  layout: { display: 'flex', gap: 60, maxWidth: 1200, margin: '0 auto', padding: '0 20px' } as any,
  content: { flex: 1, maxWidth: 780, minWidth: 0 } as any,
  tocSidebar: { width: 240, flexShrink: 0, position: 'sticky' as const, top: 40, alignSelf: 'flex-start' as const, maxHeight: 'calc(100vh - 80px)', overflowY: 'auto' as const } as any,
  tocTitle: { fontSize: 12, fontWeight: 700, textTransform: 'uppercase' as const, letterSpacing: '0.5px', color: T.textMuted, marginBottom: 12 } as any,
  tocItem: (active: boolean) => ({ display: 'block', padding: '4px 0 4px 12px', fontSize: 13, color: active ? T.primary : T.textMuted, borderLeft: `2px solid ${active ? T.primary : T.border}`, textDecoration: 'none', cursor: 'pointer', transition: 'color 0.15s' }) as any,
  tags: { display: 'flex', flexWrap: 'wrap' as const, gap: 8, margin: '32px 0' } as any,
  tagPill: { display: 'inline-block', padding: '4px 12px', borderRadius: 16, fontSize: 12, background: T.border, color: T.textSecondary } as any,
  shareRow: { display: 'flex', gap: 12, marginBottom: 40 } as any,
  shareBtn: { padding: '8px 16px', borderRadius: 8, border: `1px solid ${T.bgHover}`, background: 'transparent', color: T.textSecondary, fontSize: 13, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6 } as any,
  likeBtn: (liked: boolean) => ({ padding: '10px 24px', borderRadius: 8, border: 'none', background: liked ? `linear-gradient(135deg, ${T.danger}, #f97316)` : `linear-gradient(135deg, ${T.primary}, ${T.purple})`, color: T.text, fontSize: 14, fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8 }) as any,
  relatedSection: { borderTop: `1px solid ${T.border}`, paddingTop: 40, marginTop: 40, marginBottom: 60 } as any,
  relatedGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 20 } as any,
  relatedCard: { background: T.bgCard, border: `1px solid ${T.border}`, borderRadius: 12, padding: 20, cursor: 'pointer', transition: 'transform 0.2s' } as any,
  footer: { padding: '40px 0', borderTop: `1px solid ${T.border}`, textAlign: 'center' as const, color: T.textMuted, fontSize: 13 } as any,
};

export function BlogPost(_props: { path?: string; locale?: string; slug?: string }) {
  const locale = _props.locale || getLocale();
  const slug = _props.slug || '';
  const [post, setPost] = useState<Post | null>(null);
  const [related, setRelated] = useState<RelatedPost[]>([]);
  const [toc, setToc] = useState<TocItem[]>([]);
  const [contentHtml, setContentHtml] = useState('');
  const [liked, setLiked] = useState(false);
  const [likeCount, setLikeCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const contentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!slug) return;
    fetch(`${API_BASE}/entries/${slug}?contentType=blog&locale=${locale}`).then(r => r.json()).then(res => {
      const p = res.data?.entry;
      if (!p) { setLoading(false); return; }
      setPost(p);
      setLikeCount(p.like_count || 0);
      setRelated(res.data?.related || []);
      const { html, toc: tocItems } = renderMarkdown(p.body || '');
      setContentHtml(html);
      setToc(tocItems);
      setLoading(false);
      // Track view
      fetch(`${API_BASE}/entries/${p.id}/track`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ event_type: 'view', locale }) }).catch(() => {});
      // Set document title
      document.title = p.meta_title || p.title || 'WhiteLabelClaw Blog';
    }).catch(() => setLoading(false));
  }, [slug, locale]);

  const handleLike = () => {
    if (!post || liked) return;
    setLiked(true);
    setLikeCount(prev => prev + 1);
    fetch(`${API_BASE}/entries/${post.id}/track`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ event_type: 'click', locale }) }).catch(() => {});
  };

  const copyLink = () => {
    navigator.clipboard?.writeText(window.location.href);
  };

  const shareTwitter = () => {
    window.open(`https://twitter.com/intent/tweet?url=${encodeURIComponent(window.location.href)}&text=${encodeURIComponent(post?.title || '')}`, '_blank');
  };

  const shareLinkedIn = () => {
    window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(window.location.href)}`, '_blank');
  };

  if (loading) {
    return <div style={S.page}><div style={S.container}><div style={{ height: 400, background: T.bgCard, borderRadius: 12 }} /></div></div>;
  }

  if (!post) {
    return <div style={S.page}><div style={{ ...S.container, paddingTop: 80, textAlign: 'center' }}><h1>{locale === 'pt-BR' ? 'Post nao encontrado' : 'Post not found'}</h1><a href={`/${locale}`} style={{ color: T.primary }}>{locale === 'pt-BR' ? 'Voltar ao blog' : 'Back to blog'}</a></div></div>;
  }

  const tags = parseTags(post.tags);

  return (
    <div style={S.page}>
      <style>{`
        .blog-h2 { font-size: 24px; font-weight: 700; margin: 32px 0 16px; color: #E8EEF9; line-height: 1.3; }
        .blog-h3 { font-size: 20px; font-weight: 600; margin: 24px 0 12px; color: #E8EEF9; line-height: 1.3; }
        .blog-p { font-size: 16px; line-height: 1.8; color: #C0C8D9; margin: 0 0 16px; }
        .blog-code-block { background: #1a1e2e; border: 1px solid T.bgHover; padding: 16px; border-radius: 8; overflow-x: auto; font-family: 'JetBrains Mono', monospace; font-size: 14px; line-height: 1.6; color: #E8EEF9; margin: 16px 0; }
        .blog-inline-code { background: T.bgHover; padding: 2px 6px; border-radius: 4px; font-family: 'JetBrains Mono', monospace; font-size: 14px; color: #5B8DEF; }
        .blog-quote { border-left: 3px solid #2F6BFF; padding-left: 16px; color: #8B95A8; font-style: italic; margin: 16px 0; }
        .blog-link { color: #5B8DEF; text-decoration: none; }
        .blog-link:hover { text-decoration: underline; }
        .blog-img { max-width: 100%; border-radius: 8px; margin: 16px 0; }
        .blog-ul { padding-left: 24px; margin: 12px 0; }
        .blog-li { color: #C0C8D9; line-height: 1.8; margin: 4px 0; }
        .blog-table { width: 100%; border-collapse: collapse; margin: 16px 0; font-size: 14px; }
        .blog-table td { padding: 10px 12px; border: 1px solid T.bgHover; color: #C0C8D9; }
        .blog-table tr:first-child td { font-weight: 600; color: #E8EEF9; background: T.bgCard; }
        @media (max-width: 900px) { .blog-toc-sidebar { display: none !important; } .blog-post-layout { padding: 0 16px !important; } }
      `}</style>
      <div style={S.container}>
        <div style={S.header}>
          <a href={`/${locale}`} style={S.logo}>
            <span style={S.logoAccent}>WhiteLabelClaw</span>
            <span style={{ color: T.textMuted, fontWeight: 400 }}>Blog</span>
          </a>
        </div>
      </div>

      {/* Cover */}
      <div style={{ ...S.cover, backgroundImage: post.cover_image ? `url(${post.cover_image})` : undefined, backgroundSize: 'cover', backgroundPosition: 'center' }}>
        <div style={S.coverOverlay} />
        <div style={S.coverContent}>
          {post.category_name && <span style={S.badge(post.category_color || T.primary)}>{post.category_name}</span>}
          <h1 style={S.coverTitle}>{post.title}</h1>
          <div style={S.coverMeta}>
            <div style={S.avatar}>{(post.author_name || 'E')[0]}</div>
            <span>{post.author_name || ''}</span>
            <span>{formatDate(post.published_at, locale)}</span>
            <span style={S.readBadge}>{(() => { try { return JSON.parse(post.metadata || '{}').reading_time_min || 5; } catch { return 5; } })()} {locale === 'pt-BR' ? 'min de leitura' : 'min read'}</span>
          </div>
        </div>
      </div>

      {/* Content + TOC */}
      <div class="blog-post-layout" style={S.layout}>
        <div style={S.content}>
          <div ref={contentRef} dangerouslySetInnerHTML={{ __html: contentHtml }} />

          {/* Tags */}
          {tags.length > 0 && (
            <div style={S.tags}>
              {tags.map(tag => <span key={tag} style={S.tagPill}>{tag}</span>)}
            </div>
          )}

          {/* Share + Like */}
          <div style={S.shareRow}>
            <Button variant="ghost" style={S.shareBtn} onClick={copyLink}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M10 13a5 5 0 007.54.54l3-3a5 5 0 00-7.07-7.07l-1.72 1.71"/><path d="M14 11a5 5 0 00-7.54-.54l-3 3a5 5 0 007.07 7.07l1.71-1.71"/></svg>
              {locale === 'pt-BR' ? 'Copiar link' : 'Copy link'}
            </Button>
            <Button variant="ghost" style={S.shareBtn} onClick={shareTwitter}>X / Twitter</Button>
            <Button variant="ghost" style={S.shareBtn} onClick={shareLinkedIn}>LinkedIn</Button>
            <Button variant="ghost" style={S.likeBtn(liked)} onClick={handleLike}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill={liked ? '#fff' : 'none'} stroke="currentColor" stroke-width="2"><path d="M20.84 4.61a5.5 5.5 0 00-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 00-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 000-7.78z"/></svg>
              {likeCount}
            </Button>
          </div>

          {/* Related Posts */}
          {related.length > 0 && (
            <div style={S.relatedSection}>
              <h2 style={{ fontSize: 20, fontWeight: 700, marginBottom: 24 }}>{locale === 'pt-BR' ? 'Artigos relacionados' : 'Related articles'}</h2>
              <div style={S.relatedGrid}>
                {related.map(r => (
                  <div key={r.id} style={S.relatedCard} onClick={() => { window.location.href = `/${locale}/${r.slug}`; }}
                    onMouseOver={(e: any) => { e.currentTarget.style.transform = 'scale(1.02)'; }}
                    onMouseOut={(e: any) => { e.currentTarget.style.transform = 'scale(1)'; }}>
                    {r.category_name && <span style={S.badge(r.category_color || T.primary)}>{r.category_name}</span>}
                    <div style={{ fontSize: 16, fontWeight: 600, margin: '8px 0', lineHeight: 1.3 }}>{r.title}</div>
                    <div style={{ fontSize: 13, color: T.textMuted }}>{r.reading_time_min} min</div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* TOC Sidebar */}
        {toc.length > 0 && (
          <div class="blog-toc-sidebar" style={S.tocSidebar}>
            <div style={S.tocTitle}>{locale === 'pt-BR' ? 'Neste artigo' : 'In this article'}</div>
            {toc.map(item => (
              <a key={item.id} href={`#${item.id}`} style={{ ...S.tocItem(false), paddingLeft: item.level === 3 ? 24 : 12 }}
                onClick={(e: any) => { e.preventDefault(); document.getElementById(item.id)?.scrollIntoView({ behavior: 'smooth' }); }}>
                {item.text}
              </a>
            ))}
          </div>
        )}
      </div>

      <div style={S.container}>
        <div style={S.footer}>
          <p>{locale === 'pt-BR' ? '2026 WhiteLabelClaw. Todos os direitos reservados.' : '2026 WhiteLabelClaw. All rights reserved.'}</p>
        </div>
      </div>
    </div>
  );
}
